import * as THREE from "three";
import { FOOT_IK, type Leg } from "./footLeg";

const _s = new THREE.Vector3(), _h = new THREE.Vector3(), _k = new THREE.Vector3(), _t = new THREE.Vector3();
const _u = new THREE.Vector3(), _n = new THREE.Vector3(), _p = new THREE.Vector3(), _k2 = new THREE.Vector3();
const _a = new THREE.Vector3(), _b = new THREE.Vector3(), _piv = new THREE.Vector3();
const _q = new THREE.Quaternion(), _ql = new THREE.Quaternion(), _qp = new THREE.Quaternion(), _qi = new THREE.Quaternion();
const _up = new THREE.Vector3(), _inv2 = new THREE.Matrix4();

/**
 * Moves leg `l`'s sole to (tx, its height + `up`, tz) in the world, within what the leg can do. Solved in the space
 * of the part the leg hangs from, so the body's squash and tilt cannot pull the sole sideways: the target is mapped
 * in exactly, and when it is out of the leg's reach it is moved straight up or down (along the world vertical) to the
 * nearest point the leg can reach — never across the ground. A planted sole keeps the clip's x/z.
 */
export function placeLeg(l: Leg, up: number): void {
  const move = Math.max(-l.lengthenBy, Math.min(l.shortenBy, up));
  const last = l.lower ?? l.upper, base = l.g1.parent!;
  _s.copy(l.sole).applyMatrix4(last.matrixWorld);
  if (Math.abs(move) < 1e-5 && Math.abs(l.tx - _s.x) < 1e-5 && Math.abs(l.tz - _s.z) < 1e-5) return;
  _inv2.copy(base.matrixWorld).invert();
  _t.set(l.tx, _s.y + move, l.tz);
  _up.set(l.tx, _s.y + move + 1, l.tz).applyMatrix4(_inv2);   // the world vertical, in the base's space
  _t.applyMatrix4(_inv2);
  _up.sub(_t);
  _s.applyMatrix4(_inv2);
  _h.copy(l.upper.position);
  if (!l.lower && !l.pinned) {
    // one part, its sole only raised or lowered: stretched along the base's up about the hip and not turned, so the
    // foot keeps its shape across the ground; what the base's tilt leaves across is taken up by sliding the leg at
    // the hip (a few mm), so the sole goes straight up or down in the world
    const down = _h.y - _s.y;
    const k = down > 1e-6 ? Math.max(1 - FOOT_IK.tuck, Math.min(1 + FOOT_IK.lengthen, (_h.y - _t.y) / down)) : 1;
    l.g1.quaternion.identity();
    l.g1.scale.set(1, k, 1);
    l.g1.position.set(_t.x - _s.x, _h.y * (1 - k), _t.z - _s.z);
    return;
  }
  if (!l.lower) {
    // one part, pinned across the ground: stretched along the base's up (k), then turned so the sole lands on the pin
    _a.subVectors(_s, _h);
    const across = _a.x * _a.x + _a.z * _a.z, down = Math.abs(_a.y);
    const kMin = 1 - FOOT_IK.tuck, kMax = 1 + FOOT_IK.lengthen;
    fitReach(_t, _up, _h, Math.sqrt(across + (kMin * down) ** 2), Math.sqrt(across + (kMax * down) ** 2));
    _b.subVectors(_t, _h);
    const k = down > 1e-6 ? Math.max(kMin, Math.min(kMax, Math.sqrt(Math.max(0, _b.lengthSq() - across)) / down)) : 1;
    _a.y *= k;
    _q.setFromUnitVectors(_a.normalize(), _b.normalize());
    l.g1.quaternion.copy(_q);
    l.g1.scale.set(1, k, 1);
    l.g1.position.copy(_h).sub(_piv.copy(_h).multiply(l.g1.scale).applyQuaternion(_q));
    return;
  }
  // two parts: the knee bends (or straightens) so the sole lands on the target
  _k.copy(l.lower.position).applyMatrix4(l.upper.matrix);
  const l1 = _h.distanceTo(_k), l2 = _k.distanceTo(_s);
  fitReach(_t, _up, _h, FOOT_IK.reachMin * (l1 + l2), FOOT_IK.reachMax * (l1 + l2));
  _u.subVectors(_t, _h);
  const d = Math.max(1e-6, _u.length());
  _u.divideScalar(d);
  // the leg's plane, and which side of the hip–sole line its knee is on
  _n.crossVectors(_a.subVectors(_k, _h), _b.subVectors(_s, _h));
  if (_n.lengthSq() < 1e-12) _n.set(1, 0, 0);
  _n.normalize();
  _p.crossVectors(_n, _u).normalize();
  const side = _a.dot(_p) < 0 ? -1 : 1;
  const c = Math.max(-1, Math.min(1, (l1 * l1 + d * d - l2 * l2) / (2 * l1 * d)));
  _k2.copy(_h).addScaledVector(_u, l1 * c).addScaledVector(_p, side * l1 * Math.sqrt(1 - c * c));
  // the hip turns the thigh onto the new knee; the knee then turns the shin onto the target
  _q.setFromUnitVectors(_a.normalize(), _b.subVectors(_k2, _h).normalize());
  _a.subVectors(_s, _k).applyQuaternion(_q).normalize();
  _ql.setFromUnitVectors(_a, _b.subVectors(_t, _k2).normalize());
  l.g1.quaternion.copy(_q);
  l.g1.position.copy(_h).sub(_piv.copy(_h).applyQuaternion(_q));
  // the knee group's parent is the thigh: its turn, seen from the thigh once the hip has turned
  _qp.copy(_q).multiply(l.upper.quaternion);
  _qi.copy(_qp).invert();
  l.g2!.quaternion.copy(_qi).multiply(_ql).multiply(_qp);
  l.g2!.position.copy(l.lower.position).sub(_piv.copy(l.lower.position).applyQuaternion(l.g2!.quaternion));
}

/**
 * Moves `t` along `up` (the world vertical, in the leg's space) until it is between `min` and `max` from `hip`, by the
 * smallest move; if no point on that line is in reach, to the point of it nearest the hip (never across the ground).
 */
function fitReach(t: THREE.Vector3, up: THREE.Vector3, hip: THREE.Vector3, min: number, max: number): void {
  _r.subVectors(t, hip);
  const d = _r.length();
  if (d >= min && d <= max) return;
  const r = d > max ? max : min, uu = up.lengthSq(), au = _r.dot(up), disc = au * au - uu * (_r.lengthSq() - r * r);
  if (uu > 1e-12 && disc >= 0) {
    const s = Math.sqrt(disc), t1 = (-au - s) / uu, t2 = (-au + s) / uu;
    t.addScaledVector(up, Math.abs(t1) < Math.abs(t2) ? t1 : t2);
    return;
  }
  // no point of the line is in reach: the one nearest the hip (along the line, so the sole keeps its place across the
  // ground; pulling it toward the hip slid the golem's hind feet 1-2 cm a frame as its legs ran out of reach)
  if (uu > 1e-12) t.addScaledVector(up, -au / uu);
}
const _r = new THREE.Vector3();

